import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ToastService } from './toast.service';

/** Renders the ToastService queue as a top-anchored, screen-reader-announced stack.
 * The stack is a manual popover so it paints above the top-layer modal dialogs (sheets).
 * Visual only: под модальным <dialog> стек инертен — его нет в дереве доступности, он не
 * фокусируется и не хит-тестится (замерено), так что при открытой шторке тост не
 * озвучивается и не гасится тапом. Шторки обязаны дублировать тексты в свои внутренние
 * регионы (orderStatus/billStatus) — это не дублирование ради дублирования. */
@Component({
  selector: 'app-toast-host',
  imports: [TranslatePipe],
  template: `
    <div
      #stack
      popover="manual"
      class="toast-stack pointer-events-none fixed inset-x-0 top-0 flex flex-col items-center gap-2 px-4 pt-[max(0.75rem,env(safe-area-inset-top))]"
      role="status"
      aria-live="polite"
      aria-atomic="false"
    >
      @for (t of toasts(); track t.id) {
        <button
          type="button"
          (click)="toast.dismiss(t.id)"
          animate.enter="toast-enter"
          animate.leave="toast-leave"
          class="qm-toast pointer-events-auto w-full max-w-md rounded-2xl px-4 py-3 text-center text-sm font-semibold text-white shadow-lg ring-1 ring-black/10 transition-transform active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100"
          [class.bg-slate-900]="t.kind === 'info'"
          [class.bg-red-600]="t.kind === 'error'"
          [style.background-color]="t.kind === 'success' ? brandFill : null"
          [style.color]="t.kind === 'success' ? brandOnFill : null"
          [attr.aria-label]="t.message + '. ' + ('toast.dismiss' | translate)"
        >
          {{ t.message }}
        </button>
      }
    </div>
  `,
  styles: `
    /* Reset the popover UA box so the Tailwind classes above keep working. */
    .toast-stack {
      margin: 0;
      width: 100%;
      height: auto;
      max-height: none;
      bottom: auto;
      border: 0;
      padding-bottom: 0; /* UA [popover] padding: 0.25em; the other sides are set by px-4/pt-* */
      background: transparent;
      color: inherit;
      overflow: visible;
    }

    @keyframes toast-enter {
      from {
        opacity: 0;
        transform: translateY(-14px) scale(0.97);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    @keyframes toast-leave {
      from {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
      to {
        opacity: 0;
        transform: translateY(-10px) scale(0.97);
      }
    }

    .toast-enter {
      animation: toast-enter 260ms cubic-bezier(0.22, 1, 0.36, 1);
    }

    .toast-leave {
      animation: toast-leave 200ms ease-in forwards;
    }

    @media (prefers-reduced-motion: reduce) {
      .toast-enter,
      .toast-leave {
        animation-duration: 1ms;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastHost {
  protected toast = inject(ToastService);
  protected readonly toasts = this.toast.toasts;

  // Success follows the theme identity accent (set as CSS vars on the QR-menu host, inherited here).
  protected readonly brandFill = 'var(--qm-accent)';
  protected readonly brandOnFill = 'var(--qm-accent-fg)';

  private stack = viewChild<ElementRef<HTMLElement>>('stack');

  constructor() {
    // Optional-call: a no-op on browsers without the Popover API, where the stack
    // falls back to a normal fixed layer (and sits under an open sheet).
    afterNextRender(() => this.stack()?.nativeElement.showPopover?.());

    // The top layer paints in entry order, and a sheet's showModal() enters it long after our
    // one-time showPopover() — which is exactly how toasts ended up painted under the sheets.
    // Re-enter the layer whenever a dialog opens. Not a host binding: a dialog's toggle does
    // not bubble, so only a capture listener sees all five of them (four sheets + the viewer).
    const doc = inject(DOCUMENT);
    const restack = (event: Event) => {
      if (!(event.target instanceof HTMLDialogElement)) return;
      if ((event as ToggleEvent).newState !== 'open') return;
      const el = this.stack()?.nativeElement;
      // showPopover() alone is a no-op on an open popover — the layer has to be left first.
      el?.hidePopover?.();
      el?.showPopover?.();
    };
    doc.addEventListener('toggle', restack, true);
    inject(DestroyRef).onDestroy(() => doc.removeEventListener('toggle', restack, true));
  }
}
